// apps/cli/src/lib/onboarding-profile-store.ts
//
// The founder's two confirmed facts, held locally between the moment they are
// collected and the moment the brains exist to receive them.
//
// It needs a file rather than a variable because those two moments are in
// different PROCESSES: `m8t bootstrap profile` asks (runbook step 4b), and the
// seed runs minutes later out of `bootstrap status --watch` -> bootstrap-finalize
// -> reactiveSeedOnInstallComplete, when the install reports `done`.
//
// This replaces a source, not a destination. Everything downstream — the block
// shape, renderFounderRecord, applyProfileToBrain's commit-and-verify — is
// unchanged; only where the block COMES from moved. It used to come from a
// Foundry conversation tagged `agent="ezra-intake"` (findOnboardingProfile),
// which has had nothing to find since the intake agent stopped being deployed,
// so `memory/founder.md` stopped being written on every install.

import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import type { CanonicalOnboardingBlock } from "./onboarding-profile.js";

export const ONBOARDING_PROFILE_FILE = "onboarding-profile.json";

export interface StoredAdvisor {
  /** Either may be "" — a founder can know the name and not the address. */
  name: string;
  email: string;
}

export interface StoredOnboardingProfile {
  schemaVersion: 1;
  /** ISO-8601. Written so a later reader can say WHEN these answers were confirmed. */
  collectedAt: string;
  /** From the signed-in Azure identity — never asked. */
  founderName: string;
  /** Confirmed by the founder, never trusted raw from the UPN (guest UPNs are mangled). */
  founderEmail: string;
  /**
   * `null` is the EXPLICIT skip, and it is load-bearing. It is not the same as a
   * missing key (a malformed file) and not the same as `{name:"",email:""}` — it
   * records that we asked and they chose to skip, which renderFounderRecord turns
   * into a fillable "not captured yet" placeholder rather than a blank that reads
   * as a real answer of "nobody".
   */
  advisor: StoredAdvisor | null;
  /**
   * True only when the founder said, in so many words, that there is no advisor —
   * `--no-advisor`, or clearing one at the prompt.
   *
   * `advisor: null` alone cannot carry this. It is also what a founder who simply
   * skipped the question leaves behind, and those two need opposite treatment
   * downstream: a skip must not blank an advisor a brain already holds (that is the
   * data loss this whole mode exists to prevent), while an explicit "no" must, or
   * the founder cannot remove an address the advisor is emailing.
   */
  advisorCleared?: boolean;
}

function dir(home: string): string { return path.join(home, ".m8t"); }
function file(home: string): string { return path.join(dir(home), ONBOARDING_PROFILE_FILE); }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Validate a parsed file into the stored shape, or null.
 *
 * Deliberately total rather than partial: a half-written or hand-edited file reads
 * as "nothing was collected". Seeding is best-effort, and a partial profile committed
 * into a founder's brain is worse than no profile at all — the brain treats what it
 * finds there as authoritative (`origin: operator`).
 */
function canonical(value: unknown): StoredOnboardingProfile | null {
  if (!isRecord(value)) return null;
  if (value.schemaVersion !== 1) return null;
  if (!nonBlankString(value.founderEmail)) return null;
  if (typeof value.founderName !== "string") return null;
  if (typeof value.collectedAt !== "string") return null;
  // `undefined` is a missing key; only an explicit null is the skip.
  if (value.advisor === undefined) return null;
  let advisor: StoredAdvisor | null = null;
  if (value.advisor !== null) {
    if (!isRecord(value.advisor)) return null;
    if (typeof value.advisor.name !== "string" || typeof value.advisor.email !== "string") return null;
    advisor = { name: value.advisor.name, email: value.advisor.email };
  }
  return {
    schemaVersion: 1,
    collectedAt: value.collectedAt,
    founderName: value.founderName,
    founderEmail: value.founderEmail,
    advisor,
    ...(value.advisorCleared === true ? { advisorCleared: true } : {}),
  };
}

/** Read the collected profile, or null when there is none (or it is unreadable). */
export async function readOnboardingProfile(
  home: string = os.homedir(),
): Promise<StoredOnboardingProfile | null> {
  let raw: string;
  try {
    raw = await fs.readFile(file(home), "utf8");
  } catch {
    return null;
  }
  try {
    return canonical(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Write the collected profile, replacing any previous one. Atomic (tmp + rename). */
export async function writeOnboardingProfile(
  profile: StoredOnboardingProfile,
  home: string = os.homedir(),
): Promise<void> {
  await fs.mkdir(dir(home), { recursive: true });
  const target = file(home);
  const tmp = `${target}.tmp`;
  // 0600 like every other file this CLI puts in ~/.m8t — it holds the founder's and
  // their advisor's email addresses.
  await fs.writeFile(tmp, JSON.stringify(profile, null, 2), { encoding: "utf8", mode: 0o600 });
  await fs.rename(tmp, target);
}

/**
 * Build the onboarding block the seed path already knows how to render.
 *
 * The company fields are left UNDEFINED rather than "". onboarding-profile.ts draws a
 * real distinction there: undefined means the question was never asked, so the bullet
 * is omitted entirely; "" means it was asked and left blank, so it renders an em-dash.
 * We never ask, so undefined is the only honest value — and it keeps a row of
 * meaningless dashes out of every founder's company-profile.md.
 */
export function toOnboardingBlock(profile: StoredOnboardingProfile): CanonicalOnboardingBlock {
  return {
    schema_version: "3",
    context: "",
    founder_name: profile.founderName,
    founder_email: profile.founderEmail,
    advisor_name: profile.advisor?.name ?? "",
    advisor_email: profile.advisor?.email ?? "",
  };
}
